import RcReviews from "@/components/admin/RcReviews";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "@/lib/adminApi";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Clock, ChevronRight, Search } from "lucide-react";

const fmtWait = (mins) => {
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h} h`;
  return `${Math.floor(h / 24)} d`;
};

const KycQueue = () => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    adminApi.get("/kyc/queue")
      .then((r) => setItems(r.data || []))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load queue"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = items.filter((u) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return u.name.toLowerCase().includes(s) || (u.phone || "").includes(s) || (u.email || "").toLowerCase().includes(s);
  });

  return (
    <div className="p-8" data-testid="admin-kyc-queue">
      <div className="mb-6">
        <h1 className="font-display text-2xl font-extrabold text-slate-900">Carrier verification queue</h1>
        <p className="text-sm text-slate-500 mt-0.5">Oldest submissions first. Approve or reject after reviewing every document.</p>
      </div>

      <div className="mb-4 relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
        <Input
          data-testid="kyc-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, phone, email"
          className="pl-9 rounded-xl"
        />
      </div>

      <RcReviews/>
      {err && <div className="text-rose-700 text-sm">{err}</div>}
      {loading && <div className="text-slate-400 text-sm">Loading queue…</div>}
      {!loading && filtered.length === 0 && (
        <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-500" data-testid="kyc-queue-empty">
          Nobody in the queue right now. New submissions appear here in the order they came in.
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        {filtered.map((u, i) => (
          <Link
            key={u.id}
            to={`/admin/kyc/${u.id}`}
            data-testid={`kyc-row-${u.id}`}
            className={`flex items-center gap-4 px-5 py-4 hover:bg-slate-50 ${i > 0 ? "border-t border-slate-100" : ""}`}
          >
            <div className="w-10 h-10 rounded-full bg-slate-100 grid place-items-center font-semibold text-slate-500">
              {u.name?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-slate-900 truncate">{u.name || "Unnamed carrier"}</div>
              <div className="text-xs text-slate-500 truncate">
                {u.phone} {u.email ? `· ${u.email}` : ""} · {u.vehicle_count} vehicle{u.vehicle_count === 1 ? "" : "s"}
              </div>
            </div>
            <div className="text-right shrink-0">
              <Badge className="bg-amber-100 text-amber-800 inline-flex items-center gap-1">
                <Clock className="w-3 h-3"/> Waiting {fmtWait(u.waiting_minutes)}
              </Badge>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-300 shrink-0"/>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default KycQueue;
